/**
 * GlassCard — canonical Neon Dusk dark-glass surface primitive (glass
 * tokens; radius.lg). `hoverable` opts into the standard coral-glow hover
 * spec (DESIGN_SYSTEM.md §Motion); reduced-motion is handled globally by
 * the CssBaseline override — no JS guard needed. `as` sets the underlying
 * element ('article', 'section', ...).
 */

import { forwardRef } from 'react';
import type { ElementType, ReactNode, MouseEvent } from 'react';
import Box from '@mui/material/Box';
import type { BoxProps } from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { designTokens, motion } from '../../theme/theme';

export interface GlassCardProps extends Omit<BoxProps, 'component'> {
  /** Card body. */
  children: ReactNode;
  /** Inner padding, MUI spacing units. Default { xs: 2, md: 3 } = designTokens.spacing.card. */
  padding?: BoxProps['p'];
  /** Standard coral-glow hover spec (lift + glow + border tint). */
  hoverable?: boolean;
  /** Underlying element type; default div. */
  as?: ElementType;
  /** Optional click handler — automatically wires `role="button"` + keyboard activation when hoverable. */
  onClick?: (event: MouseEvent<HTMLElement>) => void;
}

const GlassCard = forwardRef<HTMLElement, GlassCardProps>(function GlassCard(
  { children, padding = { xs: 2, md: 3 }, hoverable = false, as = 'div', onClick, sx, ...rest },
  ref,
) {
  const isInteractive = hoverable && typeof onClick === 'function';

  const baseSx: SxProps<Theme> = {
    bgcolor: designTokens.glass.surface,
    border: `1px solid ${designTokens.glass.border}`,
    borderRadius: `${designTokens.radius.lg}px`,
    backdropFilter: designTokens.glass.blur,
    WebkitBackdropFilter: designTokens.glass.blur,
    p: padding,
    ...(hoverable && {
      cursor: isInteractive ? 'pointer' : 'default',
      transition: `transform ${motion.duration.base} ${motion.easing.standard}, box-shadow ${motion.duration.base} ${motion.easing.standard}, border-color ${motion.duration.base} ${motion.easing.standard}`,
      '&:hover': {
        transform: `translateY(${motion.hover.lift}px)`,
        boxShadow: `${designTokens.shadow.lg}, 0 0 24px rgba(232,135,92,0.08)`,
        borderColor: 'rgba(232,135,92,0.2)',
      },
      '&:focus-visible': {
        outline: 'none',
        boxShadow: designTokens.shadow.focus,
      },
    }),
  };

  /* eslint-disable xss/no-mixed-html -- React functional component returning JSX (MUI <Box/>); the `onClick?.(e as MouseEvent)` call passes a synthetic React event object to a typed callback, not an HTML string. */
  return (
    <Box
      ref={ref as React.Ref<HTMLDivElement>}
      component={as}
      onClick={onClick}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onKeyDown={
        isInteractive
          ? (e: React.KeyboardEvent<HTMLElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.(e as unknown as MouseEvent<HTMLElement>);
              }
            }
          : undefined
      }
      sx={[baseSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
      {...rest}
    >
      {children}
    </Box>
  );
  /* eslint-enable xss/no-mixed-html */
});

export default GlassCard;
